/**
 * Drill-down step generator (local engine).
 *
 * Produces a concrete, time-boxed execution roadmap for a task by matching
 * its leading verb / topic against playbook templates. The `{topic}` token
 * is replaced with the task's object phrase so steps read naturally.
 */

import type { ActionStep, ZoneId } from '../types';

interface Playbook {
  match: RegExp;
  steps: { text: string; minutes: number }[];
}

const PLAYBOOKS: Playbook[] = [
  {
    match: /\b(fix|debug|patch|resolve|troubleshoot)\b|\b(bug|crash|outage|error)\b/i,
    steps: [
      { text: 'Reproduce the issue and capture logs for {topic}', minutes: 10 },
      { text: 'Isolate the root cause (recent changes, error traces)', minutes: 15 },
      { text: 'Apply the fix and test against the failing case', minutes: 20 },
      { text: 'Deploy / verify in production and note the postmortem', minutes: 10 },
    ],
  },
  {
    match: /\b(review|read|audit|analy[sz]e|check over)\b/i,
    steps: [
      { text: 'Skim {topic} end-to-end to map its structure', minutes: 5 },
      { text: 'Deep-read the critical sections and flag concerns', minutes: 15 },
      { text: 'Write up findings as short bullet notes', minutes: 10 },
      { text: 'Decide: approve, request changes, or escalate', minutes: 5 },
    ],
  },
  {
    match: /\b(write|draft|compose|blog|article|document|report)\b/i,
    steps: [
      { text: 'Outline the 3–5 key points for {topic}', minutes: 10 },
      { text: 'Write a rough first draft — no editing yet', minutes: 25 },
      { text: 'Revise for clarity and trim 20%', minutes: 15 },
      { text: 'Final proofread and send/publish', minutes: 5 },
    ],
  },
  {
    match: /\b(call|phone|email|reply|respond|contact|follow ?up|rsvp)\b/i,
    steps: [
      { text: 'Jot the 2–3 points you must cover for {topic}', minutes: 3 },
      { text: 'Make the contact (call or send the message)', minutes: 10 },
      { text: 'Log the outcome and any follow-up action', minutes: 2 },
    ],
  },
  {
    match: /\b(plan|prepare|prep|strategy|roadmap|organi[sz]e)\b/i,
    steps: [
      { text: 'Define what "done" looks like for {topic}', minutes: 5 },
      { text: 'Break it into 3–5 milestones with rough dates', minutes: 15 },
      { text: 'Identify the single next physical action', minutes: 5 },
      { text: 'Block time on your calendar for milestone one', minutes: 5 },
    ],
  },
  {
    match: /\b(buy|order|shop|groceries|grocery|purchase|pick ?up|get)\b/i,
    steps: [
      { text: 'List exactly what you need for {topic}', minutes: 5 },
      { text: 'Order online or schedule the store run', minutes: 10 },
      { text: 'Confirm delivery/pickup and tick it off', minutes: 2 },
    ],
  },
  {
    match: /\b(meeting|1[- ]?on[- ]?1s?|sync|standup|interview|presentation|pitch|demo)\b/i,
    steps: [
      { text: 'Write the goal + 3 talking points for {topic}', minutes: 10 },
      { text: 'Prepare any materials or data you need to show', minutes: 15 },
      { text: 'Hold the session and capture decisions', minutes: 30 },
      { text: 'Send a recap with owners and next steps', minutes: 5 },
    ],
  },
  {
    match: /\b(launch|ship|release|deploy|publish|campaign)\b/i,
    steps: [
      { text: 'Run the pre-launch checklist for {topic}', minutes: 15 },
      { text: 'Stage it and do a final smoke test', minutes: 15 },
      { text: 'Go live and announce to your audience/team', minutes: 10 },
      { text: 'Monitor the first hour and log issues', minutes: 20 },
    ],
  },
  {
    match: /\b(learn|study|course|research|practice|practise)\b/i,
    steps: [
      { text: 'Pick one focused resource for {topic}', minutes: 10 },
      { text: 'Do a 25-minute distraction-free session', minutes: 25 },
      { text: 'Summarise what you learned in 3 bullets', minutes: 5 },
      { text: 'Schedule the next session before stopping', minutes: 2 },
    ],
  },
  {
    match: /\b(clean|clear|tidy|declutter|archive|unsubscribe|inbox|laundry|wash)\b/i,
    steps: [
      { text: 'Set a 15-minute timer for {topic}', minutes: 1 },
      { text: 'Do the visible/oldest items first — no rabbit holes', minutes: 15 },
      { text: 'Bin, archive, or unsubscribe from anything recurring', minutes: 5 },
    ],
  },
  {
    match: /\b(pay|bill|invoice|tax|renew|submit|file|sign)\b/i,
    steps: [
      { text: 'Gather the account details/documents for {topic}', minutes: 5 },
      { text: 'Complete the payment or submission online', minutes: 10 },
      { text: 'Save the confirmation and set the next reminder', minutes: 3 },
    ],
  },
];

/** Playbook for Zone 4 items: elimination, not execution. */
const ELIMINATION_STEPS: { text: string; minutes: number }[] = [
  { text: 'Notice the trigger that pulls you into {topic}', minutes: 2 },
  { text: 'Add one point of friction (log out, move the app, close tabs)', minutes: 3 },
  { text: 'Swap in a 5-minute replacement habit', minutes: 5 },
];

const GENERIC_STEPS: { text: string; minutes: number }[] = [
  { text: 'Clarify the outcome: what does finished look like for {topic}?', minutes: 5 },
  { text: 'Do the first 10-minute starter action right now', minutes: 10 },
  { text: 'Block calendar time for the remaining work', minutes: 5 },
  { text: 'Set a check: how will you know it is done?', minutes: 3 },
];

/** Extract the object phrase of a task, e.g. "Fix the login bug" → "the login bug". */
function topicOf(taskText: string): string {
  const words = taskText.trim().split(/\s+/);
  if (words.length <= 2) return `"${taskText.trim()}"`;
  return `"${words.slice(1).join(' ')}"`;
}

export function generateSteps(
  taskText: string,
  zone: ZoneId,
): Omit<ActionStep, 'id' | 'done'>[] {
  const template =
    zone === 'zone4'
      ? ELIMINATION_STEPS
      : (PLAYBOOKS.find((p) => p.match.test(taskText))?.steps ?? GENERIC_STEPS);
  const topic = topicOf(taskText);
  return template.map((s) => ({
    text: s.text.replace('{topic}', topic),
    minutes: s.minutes,
  }));
}
